import type { Organization, OrganizationCompleteness } from '@shared/types'

export type BusinessProfile = Organization & {
  completeness?: OrganizationCompleteness
  completenessScore?: number | null
}

export type BusinessProfileStatusState = 'linked' | 'linked_low_completeness' | 'missing'

export interface BusinessProfileStatus {
  state: BusinessProfileStatusState
  label: string
  detail: string
  profile: BusinessProfile | null
  completenessScore: number | null
  matchedDomain: string | null
}

const LOW_COMPLETENESS_THRESHOLD = 80

function normalizeDomainCandidate(value: string | undefined | null): string {
  if (!value) return ''

  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return ''

  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    return new URL(withProtocol).hostname.replace(/^\*\./, '').replace(/^www\./, '').replace(/\.$/, '')
  } catch {
    return trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .replace(/^\*\./, '')
      .replace(/^www\./, '')
      .replace(/\.$/, '')
  }
}

function domainMatches(targetDomain: string, associatedDomain: string): boolean {
  if (!targetDomain || !associatedDomain) return false
  return targetDomain === associatedDomain || targetDomain.endsWith(`.${associatedDomain}`)
}

function getCompletenessScore(profile: BusinessProfile): number | null {
  const score = profile.completeness?.score ?? profile.completenessScore ?? null
  return typeof score === 'number' && Number.isFinite(score)
    ? Math.max(0, Math.min(100, Math.round(score)))
    : null
}

function findBusinessProfileMatch(
  domain: string,
  profiles: BusinessProfile[]
): { profile: BusinessProfile; matchedDomain: string } | null {
  const targetDomain = normalizeDomainCandidate(domain)
  if (!targetDomain) return null

  let bestMatch: { profile: BusinessProfile; matchedDomain: string } | null = null

  for (const profile of profiles) {
    for (const rawAssociatedDomain of profile.associatedDomains || []) {
      const associatedDomain = normalizeDomainCandidate(rawAssociatedDomain)
      if (!domainMatches(targetDomain, associatedDomain)) continue

      if (!bestMatch || associatedDomain.length > bestMatch.matchedDomain.length) {
        bestMatch = { profile, matchedDomain: associatedDomain }
      }
    }
  }

  return bestMatch
}

export function findBusinessProfileForDomain(
  domain: string,
  profiles: BusinessProfile[]
): BusinessProfile | null {
  return findBusinessProfileMatch(domain, profiles)?.profile ?? null
}

export function getBusinessProfileStatusForDomain(
  domain: string,
  profiles: BusinessProfile[]
): BusinessProfileStatus {
  const bestMatch = findBusinessProfileMatch(domain, profiles)

  if (!bestMatch) {
    return {
      state: 'missing',
      label: 'Profile needed',
      detail: 'Add details',
      profile: null,
      completenessScore: null,
      matchedDomain: null,
    }
  }

  const completenessScore = getCompletenessScore(bestMatch.profile)
  const isLowCompleteness =
    typeof completenessScore === 'number' && completenessScore < LOW_COMPLETENESS_THRESHOLD

  return {
    state: isLowCompleteness ? 'linked_low_completeness' : 'linked',
    label: isLowCompleteness ? 'Profile needs a few more details' : 'Verified profile',
    detail: isLowCompleteness ? 'Improve details' : 'Canonical details',
    profile: bestMatch.profile,
    completenessScore,
    matchedDomain: bestMatch.matchedDomain,
  }
}
